import type { AppQueryConfig } from "QueryTypes";
import type { CourseEntitiesState } from "StoreTypes";
import type { Course, CourseWithMissingSimulatorAssignments } from "Models";
import { config } from "@/config/env";
import { clubhouseApiHeadersV1 } from "utils/api/apiHelper";

const urls = {
  simulatorCourseList(simId: string): string {
    return `${config.apiBaseUrl}/api/simulators/${simId}/courselist`;
  },
  facilityCourseList(facilityId: string): string {
    return `${config.apiBaseUrl}/api/facilities/${facilityId}/courses`;
  },
};

/**
 * Query for a simulator's courses.
 * @param simId
 */
export const getCoursesBySimQuery = (simId: string): AppQueryConfig => ({
  url: urls.simulatorCourseList(simId),
  queryKey: `getSimulatorCoursesQuery:${simId}`,
  options: { ...clubhouseApiHeadersV1() },
  transform: (responseJson: Course[]): { courses: CourseEntitiesState } => {
    const obj: { [shortName: string]: Course } = {};
    const arr: string[] = [];
    responseJson.forEach((course: Course) => {
      obj[course.shortName] = course;
      arr.push(course.shortName);
    });
    return { courses: { courseData: obj, bySimId: { [simId]: arr } } };
  },
  update: {
    courses: (oldValue, newValue): CourseEntitiesState => ({
      ...oldValue,
      courseData: {
        ...oldValue?.courseData,
        ...newValue.courseData,
      },
      bySimId: {
        ...oldValue?.bySimId,
        ...newValue.bySimId,
      },
    }),
  },
});

export const getCoursesByFacilityQuery = (
  facilityId: string,
): AppQueryConfig => ({
  url: urls.facilityCourseList(facilityId),
  queryKey: `getFacilityCoursesQuery:${facilityId}`,
  options: { ...clubhouseApiHeadersV1() },
  transform: (
    responseJson: CourseWithMissingSimulatorAssignments[],
  ): { courses: CourseEntitiesState } => {
    const courseObj: { [shortName: string]: Course } = {};
    const missingSimsObj: { [facilityId: string]: string[] } = {};
    responseJson.forEach((courseWithMissingSims) => {
      courseObj[courseWithMissingSims.course.shortName] =
        courseWithMissingSims.course;
      missingSimsObj[courseWithMissingSims.course.shortName] =
        courseWithMissingSims.missingSimulatorIds;
    });
    return {
      courses: {
        courseData: courseObj,
        missingSimulatorsByCourse: missingSimsObj,
      },
    };
  },
  update: {
    courses: (oldValue, newValue): CourseEntitiesState => ({
      ...oldValue,
      courseData: {
        ...oldValue?.courseData,
        ...newValue.courseData,
      },
      missingSimulatorsByCourse: {
        ...oldValue?.missingSimulatorsByCourse,
        ...newValue.missingSimulatorsByCourse,
      },
    }),
  },
});
